import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { settingsAPI, rechargeApiManagerAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Server, Globe, Key, Link2, Save, Plus, Trash2, Eye, EyeOff,
  RefreshCw, ChevronRight, Home, Settings, GripVertical, Power,
  PowerOff, ArrowUp, ArrowDown, X, Hash, CheckCircle, AlertCircle,
} from 'lucide-react';

// ─── Recharge API Card ───────────────────────────────
const RechargeApiCard = ({ api, onUpdate, onDelete, saving, onToggleStatus }) => {
  const [form, setForm] = useState({ ...api });
  const [showKey, setShowKey] = useState(false);
  const [showOps, setShowOps] = useState(false);
  const [newOpKey, setNewOpKey] = useState('');
  const [newOpCode, setNewOpCode] = useState('');
  const [dirty, setDirty] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { setForm({ ...api }); setDirty(false); }, [api]);

  const update = (key, val) => {
    setForm(p => ({ ...p, [key]: val }));
    setDirty(true);
  };

  const updateOpCode = (opKey, val) => {
    const codes = { ...(form.operator_codes || {}) };
    codes[opKey] = val;
    setForm(p => ({ ...p, operator_codes: codes }));
    setDirty(true);
  };

  const removeOpCode = (opKey) => {
    const codes = { ...(form.operator_codes || {}) };
    delete codes[opKey];
    setForm(p => ({ ...p, operator_codes: codes }));
    setDirty(true);
  };

  const addOpCode = () => {
    if (!newOpKey.trim() || !newOpCode.trim()) return;
    updateOpCode(newOpKey.trim().toLowerCase(), newOpCode.trim());
    setNewOpKey('');
    setNewOpCode('');
  };

  const handleSave = () => {
    const payload = { ...form };
    // Don't send masked api_key
    if (payload.api_key?.startsWith('***')) delete payload.api_key;
    onUpdate(api.id, payload);
    setDirty(false);
  };

  const handleDelete = async () => {
    if (!window.confirm(`"${api.name}" API delete করতে চান? এটা permanent!`)) return;
    setDeleting(true);
    await onDelete(api.id);
    setDeleting(false);
  };

  const opCodes = form.operator_codes || {};

  return (
    <Card className={`border shadow-sm rounded-xl transition-all ${form.status === 'active' ? 'border-emerald-200 bg-white' : 'border-slate-200 bg-slate-50 opacity-80'}`} data-testid={`api-card-${api.id}`}>
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${form.status === 'active' ? 'bg-emerald-50' : 'bg-slate-100'}`}>
              <Server className={`w-4 h-4 ${form.status === 'active' ? 'text-emerald-600' : 'text-slate-400'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <Input
                value={form.name || ''}
                onChange={e => update('name', e.target.value)}
                className="h-8 text-sm font-bold border-0 p-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
                placeholder="API Name"
                data-testid={`api-name-${api.id}`}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={form.status === 'active' ? 'default' : 'secondary'} className={`text-[10px] cursor-pointer select-none ${form.status === 'active' ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : ''}`} onClick={() => { const newStatus = form.status === 'active' ? 'inactive' : 'active'; update('status', newStatus); onToggleStatus(api.id, newStatus); }} data-testid={`api-status-${api.id}`}>
              {form.status === 'active' ? <Power className="w-2.5 h-2.5 mr-1" /> : <PowerOff className="w-2.5 h-2.5 mr-1" />}
              {form.status === 'active' ? 'ACTIVE' : 'INACTIVE'}
            </Badge>
            <span className="text-[10px] text-slate-400 font-mono">P{form.priority || 1}</span>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => update('priority', Math.max(1, (form.priority || 1) - 1))} title="Priority Up">
              <ArrowUp className="w-3 h-3" />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => update('priority', (form.priority || 1) + 1)} title="Priority Down">
              <ArrowDown className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="px-4 pb-4 space-y-3">
        {/* Row: API Code + API Key */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1"><Hash className="w-2.5 h-2.5" />API Code</label>
            <Input value={form.api_code || ''} onChange={e => update('api_code', e.target.value)} className="h-8 text-xs font-mono mt-1" placeholder="unique_api_code" data-testid={`api-code-${api.id}`} />
          </div>
          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1"><Key className="w-2.5 h-2.5" />API Key</label>
            <div className="relative mt-1">
              <Input type={showKey ? 'text' : 'password'} value={form.api_key || ''} onChange={e => update('api_key', e.target.value)} className="h-8 text-xs font-mono pr-8" placeholder="API Token/Key" data-testid={`api-key-${api.id}`} />
              <button type="button" onClick={() => setShowKey(!showKey)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showKey ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
              </button>
            </div>
          </div>
        </div>

        {/* Row: URLs */}
        <div className="space-y-2">
          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1"><Globe className="w-2.5 h-2.5" />Base URL</label>
            <Input value={form.api_url || ''} onChange={e => update('api_url', e.target.value)} className="h-8 text-xs font-mono mt-1" placeholder="http://example.com/RechargeApi" data-testid={`api-url-${api.id}`} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1"><Link2 className="w-2.5 h-2.5" />Callback URL</label>
              <Input value={form.callback_url || ''} onChange={e => update('callback_url', e.target.value)} className="h-8 text-xs font-mono mt-1" placeholder="https://..." data-testid={`callback-url-${api.id}`} />
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1"><Globe className="w-2.5 h-2.5" />Status Check URL</label>
              <Input value={form.status_check_url || ''} onChange={e => update('status_check_url', e.target.value)} className="h-8 text-xs font-mono mt-1" placeholder="http://...rechargestatus.aspx" data-testid={`status-url-${api.id}`} />
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1"><Globe className="w-2.5 h-2.5" />Balance Check URL</label>
              <Input value={form.balance_check_url || ''} onChange={e => update('balance_check_url', e.target.value)} className="h-8 text-xs font-mono mt-1" placeholder="http://...balance.aspx" data-testid={`balance-url-${api.id}`} />
            </div>
          </div>
        </div>

        {/* Operator Codes Section */}
        <div>
          <button type="button" onClick={() => setShowOps(!showOps)} className="flex items-center gap-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-wider hover:text-slate-700 transition-colors" data-testid={`toggle-ops-${api.id}`}>
            <Settings className="w-2.5 h-2.5" />
            Operator Codes ({Object.keys(opCodes).length})
            <ChevronRight className={`w-2.5 h-2.5 transition-transform ${showOps ? 'rotate-90' : ''}`} />
          </button>

          {showOps && (
            <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-2">
              {Object.entries(opCodes).map(([opKey, opVal]) => (
                <div key={opKey} className="flex items-center gap-2">
                  <span className="text-[10px] font-semibold text-slate-600 w-28 truncate uppercase" title={opKey}>{opKey}</span>
                  <Input value={opVal} onChange={e => updateOpCode(opKey, e.target.value)} className="h-7 text-xs font-mono flex-1" data-testid={`op-code-${api.id}-${opKey}`} />
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => removeOpCode(opKey)} data-testid={`remove-op-${api.id}-${opKey}`}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
              {/* Add new operator code */}
              <div className="flex items-center gap-2 pt-1 border-t border-slate-200">
                <Input value={newOpKey} onChange={e => setNewOpKey(e.target.value)} className="h-7 text-xs w-28" placeholder="operator_key" onKeyDown={e => e.key === 'Enter' && addOpCode()} />
                <Input value={newOpCode} onChange={e => setNewOpCode(e.target.value)} className="h-7 text-xs font-mono flex-1" placeholder="code" onKeyDown={e => e.key === 'Enter' && addOpCode()} />
                <Button variant="outline" size="sm" className="h-7 text-xs px-2" onClick={addOpCode} data-testid={`add-op-${api.id}`}>
                  <Plus className="w-3 h-3" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-2 border-t border-slate-100">
          <Button variant="ghost" size="sm" className="h-8 text-xs text-red-500 hover:text-red-700 hover:bg-red-50" onClick={handleDelete} disabled={deleting} data-testid={`delete-api-${api.id}`}>
            {deleting ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
            Delete
          </Button>
          <Button size="sm" className="h-8 text-xs bg-[#4680ff] hover:bg-[#2563eb] text-white" onClick={handleSave} disabled={!dirty || saving} data-testid={`save-api-${api.id}`}>
            {saving ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Plan API Section (unchanged) ───────────────────
const PLAN_FIELDS = [
  { key: 'api_token', label: 'API Token', icon: Key, sensitive: true, placeholder: 'Enter Plan API Token' },
  { key: 'balance_check_url', label: 'Balance Check URL', icon: Globe, sensitive: false, placeholder: 'https://planapionline.com/...' },
  { key: 'callback_url', label: 'Callback URL', icon: Link2, sensitive: false, placeholder: 'http://tripathytelecom.com/call_back_url' },
  { key: 'plan_url', label: 'Recharge Plan URL', icon: Globe, sensitive: false, placeholder: 'https://planapionline.com/planapi/getplan.aspx' },
  { key: 'roffer_url', label: 'Roffer URL', icon: Globe, sensitive: false, placeholder: 'https://planapionline.com/planapi/getoffer.aspx' },
  { key: 'auto_operator_url', label: 'Auto Operator URL', icon: Globe, sensitive: false, placeholder: 'https://planapionline.com/planapi/getserieslatest.aspx' },
  { key: 'postpaid_fetch_url', label: 'Postpaid Fetch URL', icon: Globe, sensitive: false, placeholder: 'https://planapionline.com/planapi/getpostpaidbill.aspx' },
  { key: 'bill_fetch_url', label: 'Bill Fetch URL', icon: Globe, sensitive: false, placeholder: 'https://planapionline.com/planapi/billinfodirect.aspx' },
  { key: 'dth_plan_url', label: 'DTH Plan URL', icon: Globe, sensitive: false, placeholder: 'https://planapionline.com/planapi/getdthplan2.aspx' },
  { key: 'dth_info_url', label: 'DTH Customer Info URL', icon: Globe, sensitive: false, placeholder: 'https://planapionline.com/planapi/dthinfodirect.aspx' },
  { key: 'dth_heavy_url', label: 'DTH Heavy Refresh URL', icon: Globe, sensitive: false, placeholder: 'https://planapionline.com/planapi/dthheavy.aspx' },
];

const PlanApiSection = ({ data, onChange, onSave, saving }) => {
  const [showSensitive, setShowSensitive] = useState(false);
  return (
    <Card className="border border-slate-100 shadow-sm rounded-xl" data-testid="section-plan-api">
      <CardHeader className="pb-3 pt-5 px-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <Globe className="w-5 h-5 text-[#4680ff]" />
            </div>
            <div>
              <CardTitle className="text-base font-bold text-slate-800">Plan API</CardTitle>
              <p className="text-xs text-slate-400 mt-0.5">planapionline.com - Plans, bill fetch, DTH, operator detection</p>
            </div>
          </div>
          <Badge variant="outline" className="text-xs">LIVE</Badge>
        </div>
      </CardHeader>
      <CardContent className="px-5 pb-5">
        <div className="space-y-4">
          {PLAN_FIELDS.map(field => {
            const Icon = field.icon;
            const isMasked = field.sensitive && (data[field.key] || '').startsWith('***');
            return (
              <div key={field.key} className="space-y-1.5">
                <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                  <Icon className="w-3 h-3" />{field.label}
                </label>
                <div className="relative">
                  <Input
                    type={field.sensitive && !showSensitive ? 'password' : 'text'}
                    value={data[field.key] || ''}
                    onChange={e => onChange(field.key, e.target.value)}
                    placeholder={field.placeholder}
                    className="h-10 text-sm font-mono bg-white border-slate-200 rounded-lg pr-10"
                  />
                  {field.sensitive && (
                    <button type="button" onClick={() => setShowSensitive(!showSensitive)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showSensitive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  )}
                </div>
                {isMasked && <p className="text-[10px] text-amber-600 font-medium">Current value masked. Enter new value to update.</p>}
              </div>
            );
          })}
        </div>
        <div className="mt-5 flex justify-end">
          <Button onClick={onSave} disabled={saving} className="bg-[#4680ff] hover:bg-[#2563eb] text-white rounded-lg h-10 px-5 text-sm font-semibold" data-testid="save-plan-api">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ─── Main Component ──────────────────────────────────
export default function ApiConfigManager() {
  const [apis, setApis] = useState([]);
  const [planData, setPlanData] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingApi, setSavingApi] = useState(null);
  const [savingPlan, setSavingPlan] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newApi, setNewApi] = useState({ name: '', api_code: '', api_url: '', api_key: '', callback_url: '', status_check_url: '', status: 'active', priority: 1 });

  const fetchData = useCallback(async () => {
    try {
      const [apisRes, configRes] = await Promise.all([
        rechargeApiManagerAPI.list(),
        settingsAPI.getApiConfig(),
      ]);
      // Sort by priority
      const sorted = (apisRes.data || []).sort((a, b) => (a.priority || 99) - (b.priority || 99));
      setApis(sorted);
      setPlanData(configRes.data.plan_api || {});
    } catch (e) {
      toast.error('Failed to load API configuration');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleToggleStatus = async (id, newStatus) => {
    try {
      await rechargeApiManagerAPI.update(id, { status: newStatus });
      toast.success(`API ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  const handleUpdateApi = async (id, data) => {
    setSavingApi(id);
    try {
      await rechargeApiManagerAPI.update(id, data);
      toast.success('API updated successfully');
      fetchData();
    } catch (e) {
      toast.error('Failed to update API');
    } finally {
      setSavingApi(null);
    }
  };

  const handleDeleteApi = async (id) => {
    try {
      await rechargeApiManagerAPI.delete(id);
      toast.success('API deleted');
      fetchData();
    } catch (e) {
      toast.error('Failed to delete API');
    }
  };

  const handleCreateApi = async () => {
    if (!newApi.name?.trim() || !newApi.api_code?.trim()) {
      toast.error('Name and API Code are required');
      return;
    }
    try {
      await rechargeApiManagerAPI.create(newApi);
      toast.success('New API added');
      setShowAddDialog(false);
      setNewApi({ name: '', api_code: '', api_url: '', api_key: '', callback_url: '', status_check_url: '', status: 'active', priority: 1 });
      fetchData();
    } catch (e) {
      toast.error('Failed to create API');
    }
  };

  const handleSavePlan = async () => {
    setSavingPlan(true);
    try {
      await settingsAPI.updateApiConfig({ section: 'plan_api', data: planData });
      toast.success('Plan API settings saved');
      fetchData();
    } catch (e) {
      toast.error('Failed to save Plan API settings');
    } finally {
      setSavingPlan(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="api-config-page">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Home className="w-4 h-4" />
          <ChevronRight className="w-3 h-3" />
          <span className="text-slate-400">Settings</span>
          <ChevronRight className="w-3 h-3" />
          <span className="font-medium text-slate-700">API Configuration</span>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">API Configuration</h1>
            <p className="text-sm text-slate-500 mt-1">Manage multiple Recharge APIs & Plan API endpoints</p>
          </div>
          <Button onClick={() => setShowAddDialog(true)} className="bg-[#4680ff] hover:bg-[#2563eb] text-white h-9 text-sm" data-testid="add-api-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Recharge API
          </Button>
        </div>

        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-48 w-full rounded-xl" />
            <Skeleton className="h-48 w-full rounded-xl" />
          </div>
        ) : (
          <div className="space-y-5">
            {/* Recharge APIs */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Server className="w-4 h-4 text-slate-500" />
                <h2 className="text-sm font-bold text-slate-600 uppercase tracking-wider">Recharge APIs ({apis.length})</h2>
              </div>

              {apis.length === 0 ? (
                <Card className="border border-dashed border-slate-200 rounded-xl">
                  <CardContent className="py-10 text-center">
                    <Server className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-400">No Recharge APIs configured</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={() => setShowAddDialog(true)}>
                      <Plus className="w-3 h-3 mr-1" /> Add First API
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-4">
                  {apis.map(api => (
                    <RechargeApiCard
                      key={api.id}
                      api={api}
                      onUpdate={handleUpdateApi}
                      onDelete={handleDeleteApi}
                      onToggleStatus={handleToggleStatus}
                      saving={savingApi === api.id}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Plan API (single config) */}
            <PlanApiSection
              data={planData}
              onChange={(key, val) => setPlanData(p => ({ ...p, [key]: val }))}
              onSave={handleSavePlan}
              saving={savingPlan}
            />
          </div>
        )}

        {/* Add New API Dialog */}
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogContent className="max-w-md" data-testid="add-api-dialog">
            <DialogHeader>
              <DialogTitle className="text-lg font-bold">Add New Recharge API</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase">API Name *</label>
                <Input value={newApi.name} onChange={e => setNewApi(p => ({ ...p, name: e.target.value }))} className="h-9 text-sm mt-1" placeholder="e.g. Backup Recharge API" data-testid="new-api-name" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase">API Code *</label>
                <Input value={newApi.api_code} onChange={e => setNewApi(p => ({ ...p, api_code: e.target.value }))} className="h-9 text-sm font-mono mt-1" placeholder="e.g. backup_api" data-testid="new-api-code" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase">Base URL</label>
                <Input value={newApi.api_url} onChange={e => setNewApi(p => ({ ...p, api_url: e.target.value }))} className="h-9 text-sm font-mono mt-1" placeholder="http://..." data-testid="new-api-url" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase">API Key</label>
                <Input value={newApi.api_key} onChange={e => setNewApi(p => ({ ...p, api_key: e.target.value }))} className="h-9 text-sm font-mono mt-1" placeholder="API Token" data-testid="new-api-key" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase">Callback URL</label>
                <Input value={newApi.callback_url} onChange={e => setNewApi(p => ({ ...p, callback_url: e.target.value }))} className="h-9 text-sm font-mono mt-1" placeholder="https://..." data-testid="new-callback-url" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase">Status Check URL</label>
                <Input value={newApi.status_check_url} onChange={e => setNewApi(p => ({ ...p, status_check_url: e.target.value }))} className="h-9 text-sm font-mono mt-1" placeholder="http://..." data-testid="new-status-url" />
              </div>
              <div>
                <label className="text-[10px] font-bold text-slate-400 uppercase">Balance Check URL</label>
                <Input value={newApi.balance_check_url || ''} onChange={e => setNewApi(p => ({ ...p, balance_check_url: e.target.value }))} className="h-9 text-sm font-mono mt-1" placeholder="http://...balance.aspx" data-testid="new-balance-url" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Priority</label>
                  <Input type="number" value={newApi.priority} onChange={e => setNewApi(p => ({ ...p, priority: parseInt(e.target.value) || 1 }))} className="h-9 text-sm mt-1" min="1" data-testid="new-api-priority" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-400 uppercase">Status</label>
                  <select value={newApi.status} onChange={e => setNewApi(p => ({ ...p, status: e.target.value }))} className="w-full h-9 text-sm mt-1 border rounded-md px-2 bg-white" data-testid="new-api-status">
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
            <DialogFooter className="mt-4">
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateApi} className="bg-[#4680ff] hover:bg-[#2563eb] text-white" data-testid="confirm-add-api">
                <Plus className="w-4 h-4 mr-1" /> Add API
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
